<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import SiteHeader from "../../components/SiteHeader.vue";
import AppImage from "../../components/AppImage.vue";
import FooterTop from "../../components/FooterTop.vue";
import FooterModal from "../../components/FooterModal.vue";
import { useCommon } from "../../composables/useCommon";
import { useTop } from "../../composables/useTop";

const common = useCommon();
const top = useTop();

const pointItems = [
  {
    img: "/assets/top/img/p_thumb_01.png",
    title: "説明書が不要なスマホ市役所",
    items: [
      "聞かれたことに答えるだけ。",
      "知っているアプリ・入っているアプリで利用可能。",
      "インストールやアカウント開設、新しいパスワードも不要。",
      "メニューを上から辿って手続きを探す必要なし。やりたいことをそのまま「伝える」だけ。AI総合案内が最適な手続きをご案内。",
    ],
    alt: "説明書が不要なスマホ市役所",
  },
  {
    img: "/assets/top/img/p_thumb_02.png",
    title: "PUSH型でご連絡。給付も",
    items: [
      "電話をかける ⇒ LINEでメッセージ送信。",
      "通知書を発送する ⇒ LINEでPDFの通知書送付。",
      "給付金を振り込む ⇒ AIで対象者と申請内容を照合・レビューし電子送金。",
      "払い込みに来てもらう ⇒ 決済ボタンを送信し電子決済。",
    ],
    alt: "PUSH型でご連絡。給付も",
  },
  {
    img: "/assets/top/img/p_thumb_03.png",
    title: "予約もすべてスマホ市役所で",
    items: [
      "施設予約を受け付ける ⇒ 予約・確認・キャンセルまでLINEで完結。個別連絡も可能。",
      "複雑な予約ルールを職員が個別対応 ⇒ 抽選・キャンセル待ち・予約対象の制御、全部自動で。",
      "鍵の受け渡しや支払いを窓口で行う ⇒ スマートロック連携・電子決済。",
      "電話で予約を受け付ける ⇒ AIが空き状況を確認し、予約受付まで対応。",
    ],
    alt: "予約もすべてスマホ市役所で",
  },
  {
    img: "/assets/top/img/p_thumb_04.png",
    title: "職員が開発できる",
    items: [
      "職員がサービスを開発できるプラットフォーム。新しいアイデアの実行に予算やプロポは不要。",
      "職員も説明書が不要に。現在の手続きを説明すれば、Copilotが構築。",
      "もはや手続きの説明も不要。申請書をアップロードしたらCopilotが構築。",
    ],
    alt: "職員が開発できる",
  },
  {
    img: "/assets/top/img/p_thumb_05.png",
    title: "全国自治体で手続きテンプレートを共有",
    items: [
      "似たような手続きは全国どこかで行われている。でも、ちょっと変更したい。そんなとき、まずコピーして、ちょっと変更可能。",
      "作った手続きが全国で利活用される。そんな「全国への貢献」の手応えを感じることができる。",
      "希望する手続きを説明すれば、Copilotが最適なテンプレートをインストール。そこからの修正もCopilotに。",
    ],
    alt: "全国自治体で手続きテンプレートを共有",
  },
];

const functionItems = [
  {
    img: "/assets/top/img/f_icon_01.png",
    alt: "PUSH型配信",
    title: "PUSH型配信",
    text: "住民一人ひとりに必要な情報をPUSH配信。LINEでメッセージ送信、通知書はPDF送信。自治会と住民をつなぐ回覧板にも対応。",
    href: "https://note.com/hashtag/GE_haishin",
  },
  {
    img: "/assets/top/img/f_icon_02.png",
    alt: "行かない窓口",
    title: "行かない窓口",
    text: "住民票や税証明の請求、JPKI認証やオンライン決済、国保脱退や水道開閉栓、犬の各種手続きなど幅広く対応できます。",
    href: "https://note.com/hashtag/GE_shoumeisho",
  },
  {
    img: "/assets/top/img/f_icon_03.png",
    alt: "施設予約",
    title: "施設予約",
    text: "抽選やキャンセル待ち、利用者ごとの制限、スマートロック連携、電子決済、リマインドや個別連絡、複雑ルールのAI作成にも対応。",
    href: "https://note.com/hashtag/GE_shisetuyoyaku",
  },
  {
    img: "/assets/top/img/f_icon_04.png",
    alt: "デジタル送金・デジタル通知",
    title: "デジタル送金・デジタル通知",
    text: "申請から通知送金までオンラインで完結。デジタル送金や支給通知、不備対応まで一気通貫で。照合審査のデジタル化や振込削減。",
    href: "https://note.com/hashtag/GE_kyuhukin",
  },
  {
    img: "/assets/top/img/f_icon_05.png",
    alt: "防災・避難所運営機能",
    title: "防災・避難所運営機能",
    text: "マイナンバーカード活用の避難所チェックインや避難所外にいる避難者把握、防災メール連携など防災アプリ同等機能を構築。",
    href: "https://note.com/hashtag/GE_bousai",
  },
  {
    img: "/assets/top/img/f_icon_06.png",
    alt: "ごみ機能",
    title: "ごみ機能",
    text: "粗大ごみ収集申込やオンライン決済、AIを使ったごみ分別の多言語化、ごみの日リマインドや環境ポイント、収集漏れ通報など。",
    href: "https://note.com/hashtag/GE_kankyogomi",
  },
  {
    img: "/assets/top/img/f_icon_07.png",
    alt: "学校機能",
    title: "学校機能",
    text: "学校欠席連絡、一斉メッセージ配信や特定の保護者へのメッセージ配信、面談やいじめ相談などの予約、学校開放事業など。",
    href: "https://note.com/hashtag/GE_gakkoukyouiku",
  },
  {
    img: "/assets/top/img/f_icon_08.png",
    alt: "子育て機能",
    title: "子育て機能",
    text: "妊娠届や交付金申請、産後ケア予約、子育て相談、問診票デジタル化や健診、一時保育予約など、妊娠期から乳幼児期まで支援。",
    href: "https://note.com/hashtag/GE_kosodate",
  },
  {
    img: "/assets/top/img/f_icon_09.png",
    alt: "検診・健診予約",
    title: "検診・健診予約",
    text: "がん検診や乳児健診の予約、対象者抽出で該当者のみ受付。日程変更やキャンセルも対応、前日リマインド配信で受診率向上。",
    href: "https://note.com/hashtag/GE_kenshin",
  },
  {
    img: "/assets/top/img/f_icon_10.png",
    alt: "通報機能",
    title: "通報機能",
    text: "道路や公園の破損、不法投棄、熊出没などを住民が通報。位置情報や写真を添付送信し、担当部署へ自動振分、状況管理にも対応。",
    href: "https://note.com/hashtag/GE_tsuho",
  },
  {
    img: "/assets/top/img/f_icon_11.png",
    alt: "ポイント機能",
    title: "ポイント機能",
    text: "健康・環境・地域活動ポイントをデジタル付与管理。付与や残高確認、利用履歴をLINE上で完結。",
    href: "https://note.com/hashtag/GE_point",
  },
  {
    img: "/assets/top/img/f_icon_12.png",
    alt: "職員専用",
    title: "職員専用",
    text: "職員向け手続きも効率化。選挙事務や参集訓練、給与明細配信や出退勤・運転日誌に対応、紙や電話業務を削減。",
    href: "https://note.com/hashtag/GE_shokuin",
  },
];

const aiItems = [
  {
    img: "/assets/top/img/ai_01.png",
    alt: "AI総合案内",
    label: "AI総合案内",
    desc: "メニューから探す手間なし。住民の用件に応じた最適サービスを自動案内。",
  },
  {
    img: "/assets/top/img/ai_02.png",
    alt: "AIチャットボット",
    label: "AIチャットボット",
    desc: "メンテナンス不要の自動応答。LINEにもホームページにも構築。",
  },
  {
    img: "/assets/top/img/ai_03.png",
    alt: "多言語化",
    label: "多言語化",
    desc: "複数言語でのメッセージ配信。ごみ分別案内の多言語対応も。",
  },
  {
    img: "/assets/top/img/ai_04.png",
    alt: "Copilot",
    label: "Copilot",
    desc: "職員が手を止めることなく、AI手続きを自動的に補助。",
  },
  {
    img: "/assets/top/img/ai_05.png",
    alt: "AIによる審査",
    label: "AIによる審査",
    desc: "書類判定・画像解析・ファイル形式などご質問に全てお応え。",
  },
];

const localGovItems = [
  { img: "/assets/top/img/c_icon_01.png", name: "群馬県" },
  { img: "/assets/top/img/c_icon_02.png", name: "広島県広島市" },
  { img: "/assets/top/img/c_icon_03.png", name: "兵庫県神戸市" },
  { img: "/assets/top/img/c_icon_04.png", name: "石川県金沢市" },
  { img: "/assets/top/img/c_icon_05.png", name: "東京都渋谷区" },
  { img: "/assets/top/img/c_icon_06.png", name: "富山県魚津市" },
  { img: "/assets/top/img/c_icon_07.png", name: "神奈川県座間市" },
  { img: "/assets/top/img/c_icon_08.png", name: "沖縄県与那原町" },
  { img: "/assets/top/img/c_icon_09.png", name: "東京都練馬区" },
  { img: "/assets/top/img/c_icon_10.png", name: "山形県庄内町" },
  { img: "/assets/top/img/c_icon_11.png", name: "三重県伊勢市" },
  { img: "/assets/top/img/c_icon_12.png", name: "東京都世田谷区" },
  { img: "/assets/top/img/c_icon_13.png", name: "神奈川県横須賀市" },
  { img: "/assets/top/img/c_icon_14.png", name: "滋賀県大津市" },
];

const openModal = (modalNum: number) => {
  common.isModalNum.value = modalNum;
  if (!common.isModalOpen.value) {
    common.modalToggle();
  }
};

const closeModal = () => {
  if (common.isModalOpen.value) {
    common.modalToggle();
  }
};

type CarouselKind = "news" | "case";
type CarouselDirection = "prev" | "next";

const newsSlideIndex = ref(0);
const caseSlideIndex = ref(0);
const aiSlideIndex = ref(0);
const aiItemsEl = ref<HTMLElement | null>(null);

const onAiScroll = () => {
  const el = aiItemsEl.value;
  if (!el) return;
  const first = el.firstElementChild as HTMLElement | null;
  if (!first) return;
  const second = first.nextElementSibling as HTMLElement | null;
  const gap = second
    ? Math.max(0, second.getBoundingClientRect().left - first.getBoundingClientRect().right)
    : 0;
  const step = first.getBoundingClientRect().width + gap;
  if (!step) return;
  aiSlideIndex.value = Math.round(el.scrollLeft / step);
};

const goToAiSlide = (index: number) => {
  const el = aiItemsEl.value;
  if (!el) return;
  const first = el.firstElementChild as HTMLElement | null;
  if (!first) return;
  const second = first.nextElementSibling as HTMLElement | null;
  const gap = second
    ? Math.max(0, second.getBoundingClientRect().left - first.getBoundingClientRect().right)
    : 0;
  const step = first.getBoundingClientRect().width + gap;
  el.scrollTo({ left: index * step, behavior: "smooth" });
};

const getCarouselElements = (kind: CarouselKind) => {
  if (kind === "news") {
    return {
      wrap: document.querySelector(
        ".top-news-slide-wrap",
      ) as HTMLElement | null,
      track: document.querySelector(".top-news-slide") as HTMLElement | null,
      itemSelector: ".top-news-slide__li",
      indexRef: newsSlideIndex,
    };
  }
  return {
    wrap: document.querySelector(".top-case-slide-wrap") as HTMLElement | null,
    track: document.querySelector(".top-case-slide") as HTMLElement | null,
    itemSelector: ".top-case-slide__li",
    indexRef: caseSlideIndex,
  };
};

const applyCarouselPosition = (kind: CarouselKind) => {
  const { wrap, track, itemSelector, indexRef } = getCarouselElements(kind);
  if (!wrap || !track) {
    return;
  }

  const firstItem = track.querySelector(itemSelector) as HTMLElement | null;
  if (!firstItem) {
    track.style.transform = "translate3d(0, 0, 0)";
    indexRef.value = 0;
    return;
  }

  const secondItem = firstItem.nextElementSibling as HTMLElement | null;
  const firstRect = firstItem.getBoundingClientRect();
  const secondRect = secondItem?.getBoundingClientRect();
  const gap = secondRect ? Math.max(0, secondRect.left - firstRect.right) : 0;
  const step = firstRect.width + gap;
  if (!step) {
    return;
  }

  const maxScroll = Math.max(0, track.scrollWidth - wrap.clientWidth);
  const maxIndex = Math.max(0, Math.ceil(maxScroll / step));
  if (indexRef.value > maxIndex) {
    indexRef.value = maxIndex;
  }
  if (indexRef.value < 0) {
    indexRef.value = 0;
  }

  track.style.transition = "transform .35s ease";
  track.style.transform = `translate3d(${-indexRef.value * step}px, 0, 0)`;
};

const moveCarousel = (kind: CarouselKind, direction: CarouselDirection) => {
  const indexRef = kind === "news" ? newsSlideIndex : caseSlideIndex;
  indexRef.value += direction === "next" ? 1 : -1;
  applyCarouselPosition(kind);
};

const syncCarousels = () => {
  applyCarouselPosition("news");
  applyCarouselPosition("case");
};

onMounted(async () => {
  await nextTick();
  syncCarousels();
  window.addEventListener("resize", syncCarousels);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", syncCarousels);
});

watch(
  () => top.newsItems.value.length,
  async () => {
    await nextTick();
    applyCarouselPosition("news");
  },
);

watch(
  () => top.caseItems.value.length,
  async () => {
    await nextTick();
    applyCarouselPosition("case");
  },
);
</script>

<template>
  <div
    class="be-wrapper"
    :class="{ 'is-menuopen': common.isMenuOpen.value }"
    v-cloak
  >
    <SiteHeader @toggle-menu="common.menuToggle" />

    <main id="be-content" class="be-content">
      <div class="top-hero">
        <p class="top-hero__text">
          <picture>
            <source
              srcset="/assets/top/img/hero-text.svg"
              media="(max-width: 768px)"
              width="283"
              height="200"
            />
            <source
              srcset="/assets/top/img/hero-text_pc.svg"
              media="(min-width: 769px)"
              width="277"
              height="61"
            />
            <AppImage
              src="/assets/top/img/hero-text_pc.svg"
              alt="もっと愛される行政サービスをつくろう。"
              width="283"
              height="200"
              class="be-resize"
            />
          </picture>
        </p>
        <div class="top-hero__thumb">
          <video
            autoplay
            loop
            muted
            playsinline
            disablepictureinpicture
            preload="auto"
            fetchpriority="high"
            width="1000"
            height="1000"
            class="be-resize"
            aria-label="もっと愛される行政サービスをつくろう。"
          >
            <source
              src="/assets/top/img/bot_express_animated_sp.mov"
              type='video/quicktime; codecs="hvc1"'
              media="(max-width: 768px)"
            />
            <source
              src="/assets/top/img/bot_express_animated.mov"
              type='video/quicktime; codecs="hvc1"'
            />
            <source
              src="/assets/top/img/bot_express_animated_sp.webm"
              type="video/webm"
              media="(max-width: 768px)"
            />
            <source
              src="/assets/top/img/bot_express_animated.webm"
              type="video/webm"
            />
            <source
              src="/assets/top/img/bot_express_animated_sp.mp4"
              type="video/mp4"
              media="(max-width: 768px)"
            />
            <source
              src="/assets/top/img/bot_express_animated.mp4"
              type="video/mp4"
            />
          </video>
        </div>
        <a
          v-for="item in top.latest.value"
          :key="item.xmllink"
          :href="item.xmllink"
          target="_blank"
          rel="noopener"
          class="top-hero__news"
        >
          <div class="top-hero__news__head">
            <span class="top-hero__news__date font-en">{{
              item.xmlpubdate
            }}</span>
            <span class="top-hero__news__latest">最新情報</span>
          </div>
          <div class="top-hero__news__text">{{ item.xmltitle }}</div>
          <div class="top-hero__news__arrow">
            <span class="be-icon--arrow2"></span>
          </div>
        </a>
        <a
          v-if="top.latest.value.length === 0"
          href="/news/"
          class="top-hero__news"
        >
          <div class="top-hero__news__head">
            <span class="top-hero__news__date font-en">Latest</span>
            <span class="top-hero__news__latest">最新情報</span>
          </div>
          <div class="top-hero__news__text">お知らせ一覧を見る</div>
          <div class="top-hero__news__arrow">
            <span class="be-icon--arrow2"></span>
          </div>
        </a>
        <div class="top-hero__loop"></div>
      </div>

      <div class="top-point">
        <div class="top-point00">
          <div class="top-point__bg1 js-scrollfade"></div>
          <div class="top-point00__inner">
            <div class="top-point00__l js-scrollfade">
              <AppImage
                src="/assets/top/img/thumb_01.png"
                alt="株式会社Bot Express"
                width="1822"
                height="1366"
                class="be-resize"
              />
            </div>
            <div class="top-point00__r">
              <h2 class="top-point00__title js-scrollfade">
                市役所・病院・学校。<br />町のすべての窓口を、<br />スマホの中に。
              </h2>
              <p class="top-point00__text js-scrollfade">
                24時間365日、ほとんどの住民が、今、すぐに利用できる「スマホ市役所」を提供。役所の申請や手続き、学校の欠席連絡や病院の予約など、町のあらゆる窓口機能をスマホから利用可能にし、好きな時に町とコミュニケーションできる未来を実現します。住民目線で使いやすいサービスにするために、職員自ら自由に開発し、改善を続けることも可能。他社ツールとも接続でき、初期の開発費用をかけずに導入できます。
              </p>
            </div>
          </div>
        </div>

        <div class="top-point10">
          <div class="top-point-clm">
            <div
              class="top-point-clm__li js-scrollfade"
              v-for="item in pointItems"
              :key="item.img"
            >
              <div class="top-point-block">
                <div class="top-point-block__thumb">
                  <AppImage
                    :src="item.img"
                    :alt="item.alt"
                    width="480"
                    height="480"
                    class="be-resize"
                  />
                </div>
                <div class="top-point-block__content">
                  <h2 class="top-point-block__title" v-html="item.title"></h2>
                  <ul class="top-point-block__list">
                    <li
                      v-for="(li, i) in item.items"
                      :key="i"
                      class="top-point-block__list-item"
                    >
                      {{ li }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="top-point__bg2"></div>
      </div>

      <div class="top-function">
        <div class="top-inner">
          <h2 class="top-function__title js-scrollfade">
            <span class="font-en">Function<small>○</small></span>
            <span class="top-function__title__sub">機能紹介</span>
          </h2>

          <div>
            <h3 class="top-function__subtitle js-scrollfade">
              <i></i
              ><span
                >申請・予約手続きを1問1答の対話型で実装。機能の組み合わせも自由</span
              >
            </h3>
            <div
              class="top-function-acd"
              :class="{ 'is-acdopen': top.isAcdOpen.value }"
            >
              <div class="top-function-3clm">
                <div
                  class="top-function-3clm__li js-scrollfade"
                  v-for="item in functionItems"
                  :key="item.img"
                >
                  <a
                    :href="item.href"
                    target="_blank"
                    rel="noopener"
                    class="top-function-btn"
                  >
                    <div class="top-function-btn__thumb">
                      <AppImage
                        :src="item.img"
                        :alt="item.alt"
                        width="160"
                        height="160"
                        class="be-resize"
                      />
                    </div>
                    <div class="top-function-btn__content">
                      <div class="top-function-btn__title">
                        {{ item.title }}
                      </div>
                      <div class="top-function-btn__text">{{ item.text }}</div>
                    </div>
                    <div class="top-function-btn__arrow"></div>
                  </a>
                </div>
              </div>
              <div class="top-function-acd__btn" @click="top.acdToggle()">
                <span v-if="top.isAcdOpen.value === false"
                  >表示を増やす <i></i
                ></span>
                <span v-else>表示を減らす <i class="is-close"></i></span>
              </div>
            </div>
          </div>

          <div class="top-function-ai">
            <AppImage
              src="/assets/top/img/sumahoshiyakusho_ai.png"
              alt="スマホ市役所AI"
              width="1500"
              height="186"
              class="top-function-ai__logo"
            />
            <div
              ref="aiItemsEl"
              class="top-function-ai__items"
              @scroll.passive="onAiScroll"
            >
              <div
                v-for="item in aiItems"
                :key="item.alt"
                class="top-function-ai__item js-scrollfade"
              >
                <div class="top-function-ai__icon">
                  <AppImage :src="item.img" :alt="item.alt" />
                </div>
                <p class="top-function-ai__label">{{ item.label }}</p>
                <p class="top-function-ai__desc">{{ item.desc }}</p>
              </div>
            </div>
            <div class="top-function-ai__dots">
              <button
                v-for="(_, i) in aiItems"
                :key="i"
                type="button"
                class="top-function-ai__dot"
                :class="{ 'is-active': aiSlideIndex === i }"
                :aria-label="`スライド${i + 1}`"
                @click="goToAiSlide(i)"
              />
            </div>
          </div>

          <div class="top-function-section">
            <h3 class="top-function__subtitle js-scrollfade">
              <i style="background-color: #0b6446"></i
              ><span>直感的に操作できる管理画面</span>
            </h3>
            <div class="top-function-block">
              <div class="top-function-block__l">
                <div class="top-function-block__l__bg js-scrollfade"></div>
                <AppImage
                  src="/assets/top/img/f_thumb_01.png"
                  alt="住民からの申請は自動的に状態ごとのボックスに振り分け。"
                  width="1348"
                  height="799"
                  class="be-resize js-scrollfade"
                />
              </div>
              <div class="top-function-block__r">
                <h4 class="top-function-block__title js-scrollfade">
                  住民からの申請は<br class="be-hidden-sp" />自動的に状態ごとの<br
                    class="be-hidden-sp"
                  />ボックスに振り分け。
                </h4>
                <p class="top-function-block__text js-scrollfade">
                  住民票や税証明書などはもちろん、各種申請はトークにて行なわれ、自動的にグルーピングされます。ステータス管理が一目でわかるので、直感的に操作しやすい管理画面です。
                </p>
              </div>
            </div>
            <div class="top-function-block">
              <div class="top-function-block__l top-function-block__l02">
                <div class="top-function-block__l02__bg js-scrollfade"></div>
                <AppImage
                  src="/assets/top/img/f_thumb_02.png"
                  alt="LINEでアンケート。数時間で町の状況や住民の意見を集約できます。"
                  width="1508"
                  height="799"
                  class="be-resize js-scrollfade"
                />
              </div>
              <div class="top-function-block__r">
                <h4 class="top-function-block__title js-scrollfade">
                  LINEでアンケート。<br class="be-hidden-sp" />数時間で町の状況や<br
                    class="be-hidden-sp"
                  />住民の意見を集約できます。
                </h4>
                <p class="top-function-block__text js-scrollfade">
                  LINEでのやりとりの手軽さでより多くの方の意見を聞くことができます。自動集計で町の状況把握まで時間がかかりません。緊急性の高い事柄などその時にあった質問も可能です。
                </p>
              </div>
            </div>
            <div class="top-function-block">
              <div class="top-function-block__l top-function-block__l03">
                <div class="top-function-block__l03__bg js-scrollfade"></div>
                <AppImage
                  src="/assets/top/img/f_thumb_03.png"
                  alt="全自動集計。あらゆる角度のレポートが用意されています。"
                  width="1223"
                  height="799"
                  class="be-resize js-scrollfade"
                />
              </div>
              <div class="top-function-block__r">
                <h4 class="top-function-block__title js-scrollfade">
                  全自動集計。<br class="be-hidden-sp" />あらゆる角度のレポートが<br
                    class="be-hidden-sp"
                  />用意されています。
                </h4>
                <p class="top-function-block__text js-scrollfade">
                  申請年月/種別/手数料などあらゆる角度からのレポートが自動集計/分析されます。欲しい情報の絞り込みもでき、住民のニーズに合わせた自治体機能のアップデートを計画できます。
                </p>
              </div>
            </div>
          </div>

          <div class="top-function-section">
            <h3 class="top-function__subtitle js-scrollfade">
              <i style="background-color: #0b6446"></i
              ><span>環境を変えずに、信頼できるセキュリティを</span>
            </h3>
            <div class="top-function-2clm">
              <div class="top-function-2clm__li">
                <div class="top-function-security">
                  <div
                    class="top-function-security__thumb top-function-security__thumb01 js-scrollfade"
                  >
                    <AppImage
                      src="/assets/top/img/f_thumb_10.png"
                      alt="LGWANからも利用可能。"
                      width="487"
                      height="178"
                      class="be-resize"
                    />
                  </div>
                  <div class="top-function-security__content">
                    <h4 class="top-function-security__title js-scrollfade">
                      LGWANからも利用可能。
                    </h4>
                    <p class="top-function-security__text js-scrollfade">
                      GovTech
                      ExpressはLGWAN（総合行政ネットワーク）からも利用可能。今お使いのネットワークやパソコンからご利用いただけます。
                      <span style="font-size: 0.8em">
                        <br />※LGWAN接続は別途費用が発生します。
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div class="top-function-2clm__li">
                <div class="top-function-security">
                  <div
                    class="top-function-security__thumb top-function-security__thumb02 js-scrollfade"
                  >
                    <AppImage
                      src="/assets/top/img/f_thumb_11.png"
                      alt="認定サービスでセキュリティも安心。"
                      width="494"
                      height="178"
                      class="be-resize"
                    />
                  </div>
                  <div class="top-function-security__content">
                    <h4 class="top-function-security__title js-scrollfade">
                      認定サービスでセキュリティも安心。
                    </h4>
                    <p class="top-function-security__text js-scrollfade">
                      データはISO27001、ISO27017、ISO27018の国際認証、および総務省の「クラウドサービスの安全・信頼性に関わる情報開示認定」を受けているクラウドサービス（日本のデータセンター）に保存。専用環境で稼働するため、性能的にもセキュリティ的にも他の環境の影響を受けません。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="top-case">
        <div class="top-inner">
          <h2 class="top-case__title js-scrollfade">
            <span class="font-en"><small>○ </small>Case study</span>
            <span class="top-case__title__sub">導入事例と利用実績</span>
          </h2>
          <div class="top-case__other js-scrollfade">
            <a
              href="https://note.bot-express.com/m/m06f8498f3ab1"
              target="_blank"
              rel="noopener"
              class="be-button--text be-bold"
              ><span>その他の実績はこちら</span
              ><span class="be-icon--arrow2"></span
            ></a>
          </div>

          <div class="top-case-slide-wrap">
            <div class="top-case-slide">
              <div
                class="top-case-slide__li"
                v-for="item in top.caseItems.value"
                :key="item.xmllink"
              >
                <a
                  :href="item.xmllink"
                  target="_blank"
                  rel="noopener"
                  class="top-case-block"
                >
                  <div class="top-case-block__thumb">
                    <AppImage
                      :src="item.xmlimage || '/assets/common/img/ogp.png'"
                      :alt="item.xmltitle"
                      width="800"
                      height="419"
                      class="be-resize js-scrollfade"
                    />
                    <div class="top-case-block__thumb__bg js-scrollfade"></div>
                  </div>
                  <div class="top-case-block__content">
                    <h3 class="top-case-block__title js-scrollfade">
                      {{ item.xmltitle }}
                    </h3>
                    <p class="top-case-block__text js-scrollfade">
                      {{ item.xmldescription }}
                    </p>
                    <div class="top-case-block__arrow js-scrollfade">
                      <span class="be-icon--arrow2"></span>
                    </div>
                  </div>
                </a>
              </div>
            </div>
            <div class="top-case-slide__nav">
              <div
                class="top-case-slide__button-prev"
                aria-hidden="true"
                @click="moveCarousel('case', 'prev')"
              >
                <span class="be-icon--arrow"></span>
              </div>
              <div
                class="top-case-slide__button-next"
                aria-hidden="true"
                @click="moveCarousel('case', 'next')"
              >
                <span class="be-icon--arrow"></span>
              </div>
            </div>
          </div>

          <h3 class="top-function__subtitle js-scrollfade">
            <i style="background-color: #0b6446"></i
            ><span
              >350以上の自治体（市区町村、都道府県、省庁）がアクティブに使っています</span
            >
          </h3>
          <div style="text-align: center">
            <a
              target="_blank"
              rel="noopener"
              class="be-button--text be-bold top-case-button__text"
              href="https://note.bot-express.com/n/naeaa32eb2b4b"
              >導入自治体一覧はこちら<span class="be-icon--arrow2"></span
            ></a>
          </div>
        </div>
        <div class="top-case-loop">
          <div class="top-case-loop-wrap">
            <ul class="top-case-loop-list" v-for="loopNum in 3" :key="loopNum">
              <li
                class="top-case-loop-list__li"
                v-for="item in localGovItems"
                :key="`${loopNum}-${item.name}`"
              >
                <AppImage
                  :src="item.img"
                  :alt="item.name"
                  width="292"
                  height="176"
                  class="be-resize"
                />
                <p class="top-case-loop-list__text">{{ item.name }}</p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="top-news">
        <div class="top-inner">
          <div class="top-news__flex">
            <h2 class="top-news__title js-scrollfade">
              <span class="font-en"><small>○&nbsp;&nbsp;</small>News</span>
              <span class="top-news__title__sub">お知らせ</span>
            </h2>
            <div class="top-news__flex__r js-scrollfade">
              <div
                class="top-news-slide__button-prev"
                aria-hidden="true"
                @click="moveCarousel('news', 'prev')"
              >
                <span class="be-icon--arrow"></span>
              </div>
              <div
                class="top-news-slide__button-next"
                aria-hidden="true"
                @click="moveCarousel('news', 'next')"
              >
                <span class="be-icon--arrow"></span>
              </div>
            </div>
          </div>
        </div>
        <div class="top-news-slide-wrap js-scrollfade">
          <div class="top-news-slide">
            <div
              class="top-news-slide__li"
              v-for="item in top.newsItems.value"
              :key="item.xmllink"
            >
              <a
                :href="item.xmllink"
                target="_blank"
                rel="noopener"
                class="top-news-block"
              >
                <div class="top-news-block__thumb">
                  <AppImage
                    :src="item.xmlimage || '/assets/common/img/ogp.png'"
                    :alt="item.xmltitle"
                    width="800"
                    height="419"
                    class="be-resize"
                  />
                </div>
                <div class="top-news-block__head">
                  <span class="top-news-block__date font-en">{{
                    item.xmlpubdate
                  }}</span>
                </div>
                <p class="top-news-block__text">{{ item.xmltitle }}</p>
              </a>
            </div>
          </div>
        </div>
        <div class="top-inner">
          <div class="top-news__btn js-scrollfade">
            <a href="/news/" class="be-button--white"
              ><span>一覧をみる</span></a
            >
          </div>
        </div>
      </div>
    </main>

    <FooterTop @open-modal="openModal" />
    <FooterModal
      :is-open="common.isModalOpen.value"
      :modal-num="common.isModalNum.value"
      @close="closeModal"
    />
  </div>
</template>

<style>
.top-case-slide__li,
.top-news-slide__li {
  flex: 0 0 auto;
}

@media only screen and (max-width: 768px) {
  .top-case-slide-wrap,
  .top-news-slide-wrap {
    overflow: hidden;
  }

  .top-case-slide__li + .top-case-slide__li,
  .top-news-slide__li + .top-news-slide__li {
    margin-left: calc(10 / 375 * 100vw);
  }
}

.top-function-ai__items {
  @media only screen and (min-width: 769px) {
    display: flex !important;
    flex-wrap: wrap;
    justify-content: center;
    gap: calc(30 / 1600 * 100vw);
  }

  @media only screen and (max-width: 768px) {
    display: flex !important;
    flex-direction: row;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    gap: calc(16 / 375 * 100vw);
    padding: 0 calc(48 / 375 * 100vw);

    &::-webkit-scrollbar {
      display: none;
    }
  }
}

.top-function-ai__item {
  @media only screen and (max-width: 768px) {
    scroll-snap-align: center;
    flex: 0 0 auto;
  }
}

.top-function-ai__dots {
  display: flex;
  justify-content: center;
  gap: calc(8 / 375 * 100vw);
  margin-top: calc(20 / 375 * 100vw);

  @media only screen and (min-width: 769px) {
    display: none;
  }
}

.top-function-ai__dot {
  position: relative;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}

.top-function-ai__dot::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(10 / 375 * 100vw);
  height: calc(10 / 375 * 100vw);
  border-radius: 50%;
  background: #d9d9d9;
  transform: translate(-50%, -50%);
  transition: background 0.2s ease;
}

.top-function-ai__dot.is-active::before {
  background: #007aff;
}

.top-point-block__list {
  font-size: 1.4rem;
  line-height: 1.9;
  margin: 0.65em 0 0;
  padding: 0 6.4vw;
  list-style: none;
}
.top-point-block__list-item {
  position: relative;
  padding-left: 1.1em;
  text-align: left;
}
.top-point-block__list-item + .top-point-block__list-item {
  margin-top: 0.3em;
}
.top-point-block__list-item::before {
  content: "";
  position: absolute;
  top: 0.75em;
  left: 0.15em;
  width: 0.45em;
  height: 0.45em;
  border-radius: 50%;
  background: currentColor;
}

@media only screen and (min-width: 769px) {
  .top-point-clm {
    flex-direction: column;
    align-items: stretch;
    flex-wrap: nowrap;
    gap: 6vw;
    max-width: 78vw;
  }
  .top-point-clm__li,
  .top-point-clm__li:nth-child(2),
  .top-point-clm__li:nth-child(4),
  .top-point-clm__li:nth-child(5) {
    width: 100%;
    margin: 0;
  }
  .top-point-clm__li + .top-point-clm__li {
    margin-top: 0;
  }
  .top-point-block {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 5vw;
  }
  .top-point-clm__li:nth-child(even) .top-point-block {
    flex-direction: row-reverse;
  }
  .top-point-block__thumb {
    width: 32vw;
    flex: 0 0 32vw;
    margin: 0;
  }
  .top-point-block__content {
    flex: 1;
    min-width: 0;
  }
  .top-point-block__title {
    font-size: 3.2rem;
    text-align: left;
    margin-top: 0;
    line-height: 1.5;
    padding: 0;
  }
  .top-point-block__text {
    font-size: 1.8rem;
    text-align: left;
    margin: 1.2em 0 0;
    max-width: none;
    padding: 0;
  }
  .top-point-block__list {
    font-size: 1.7rem;
    line-height: 1.7;
    margin: 1em 0 0;
    padding: 0;
  }
  .top-point-block__list-item {
    padding-left: 1em;
  }
  .top-point-block__list-item + .top-point-block__list-item {
    margin-top: 0.35em;
  }
  .top-point-block__list-item::before {
    top: 0.7em;
    left: 0;
    width: 0.4em;
    height: 0.4em;
  }
}
</style>
